import { createContext, useContext, useMemo } from 'react';
import type {
  UseApiKeyCreate,
  UseApiKeyDelete,
  UseCreateOrganization,
  UseDspListFetch,
  UseDspSeatListFetch,
  UseEditOrganization,
  UseGetDefaultParentOrganizationId,
  UseGetOrganizationsTableData,
  UseOrganizationApiKeysFetch,
  UseOrganizationFetch,
  UseOrganizationPageHeaderActions,
  UseOrganizationPermissions,
  UseOrganizationPixelMetricsFetch,
  UseOrganizationsListFetch,
  UseOrganizationUsersFetch,
} from './OrganizationHookTypes.ts';

export type OrganizationProviderConfig = {
  isOa?: boolean;
  useOrganizationFetch: UseOrganizationFetch;
  useOrganizationsListFetch: UseOrganizationsListFetch;
  useOrganizationUsersFetch: UseOrganizationUsersFetch;
  useOrganizationPageHeaderActions: UseOrganizationPageHeaderActions;
  useGetOrganizationsTableData: UseGetOrganizationsTableData;
  useOrganizationApiKeysFetch: UseOrganizationApiKeysFetch;
  useOrganizationPixelMetricsFetch: UseOrganizationPixelMetricsFetch;
  useApiKeyDelete: UseApiKeyDelete;
  useGetDefaultParentOrganizationId: UseGetDefaultParentOrganizationId;
  useCreateOrganization: UseCreateOrganization;
  useEditOrganization: UseEditOrganization;
  useOrganizationPermissions: UseOrganizationPermissions;
  useApiKeyCreate: UseApiKeyCreate;
  useDspsFetch: UseDspListFetch;
  useDspSeatsFetch: UseDspSeatListFetch;
};

export type OrganizationProviderProps = {
  children: React.ReactNode;
  organizationConfig: OrganizationProviderConfig;
};

const organizationContext = createContext<OrganizationProviderConfig>({
  isOa: false,
  useOrganizationFetch: () => {
    throw new Error('useOrganizationFetch: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useOrganizationsListFetch: () => {
    throw new Error('useOrganizationsListFetch: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useOrganizationUsersFetch: () => {
    throw new Error('useOrganizationUsersFetch: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useOrganizationPageHeaderActions: () => {
    throw new Error('useOrganizationPageHeaderActions: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useGetOrganizationsTableData: () => {
    throw new Error('useGetOrganizationsTableData: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useOrganizationApiKeysFetch: () => {
    throw new Error('useOrganizationApiKeysFetch: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useOrganizationPixelMetricsFetch: () => {
    throw new Error('useOrganizationPixelMetricsFetch: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useApiKeyDelete: () => {
    throw new Error('useApiKeyDelete: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useGetDefaultParentOrganizationId: () => {
    throw new Error(
      'useGetDefaultParentOrganizationId: Not implemented. Ensure you are inside the OrganizationContext'
    );
  },
  useCreateOrganization: () => {
    throw new Error('useCreateOrganization: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useEditOrganization: () => {
    throw new Error('useEditOrganization: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useOrganizationPermissions: () => {
    throw new Error('useOrganizationsPermissions: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useApiKeyCreate: () => {
    throw new Error('useApiKeyCreate: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useDspsFetch: () => {
    throw new Error('useDspsFetch: Not implemented. Ensure you are inside the OrganizationContext');
  },
  useDspSeatsFetch: () => {
    throw new Error('useDspSeatsFetch: Not implemented. Ensure you are inside the OrganizationContext');
  },
});

export const useOrganizationContext = () => useContext(organizationContext);

export function OrganizationProvider({ children, organizationConfig }: OrganizationProviderProps) {
  const value: OrganizationProviderConfig = useMemo(
    () => ({
      ...organizationConfig,
    }),
    [organizationConfig]
  );

  return <organizationContext.Provider value={value}>{children}</organizationContext.Provider>;
}
